import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { DocumentRowUi } from "../../types/documentManagementUiTypes";
import { isDocumentRolledBack } from "../../services/ragDocumentApi";

const actionMenuPaperSx = {
  mt: 0.5,
  overflow: "visible",
  backgroundColor: "#f5f5f9",
  border: "1px solid #dadde9",
  boxShadow: "none",
  borderRadius: 1,
  px: 2,
  py: 1.5,
  minWidth: "10rem",
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    top: -6,
    left: "50%",
    transform: "translateX(-50%)",
    width: 0,
    height: 0,
    borderLeft: "6px solid transparent",
    borderRight: "6px solid transparent",
    borderBottom: "6px solid #dadde9",
  },
  "&::after": {
    content: '""',
    display: "block",
    position: "absolute",
    top: -5,
    left: "50%",
    transform: "translateX(-50%)",
    width: 0,
    height: 0,
    borderLeft: "5px solid transparent",
    borderRight: "5px solid transparent",
    borderBottom: "5px solid #f5f5f9",
  },
} as const;

const actionMenuItemSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 0,
  py: 0.5,
  minHeight: "auto",
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: "transparent",
  },
} as const;

export type DocumentLibraryRowActionsProps = {
  row: DocumentRowUi;
  disabled: boolean;
  onViewEdit: (row: DocumentRowUi) => void;
  onDelete: (row: DocumentRowUi) => void;
  onRetryProcessing?: (row: DocumentRowUi) => void;
};

export function DocumentLibraryRowActions({
  row,
  disabled,
  onViewEdit,
  onDelete,
  onRetryProcessing,
}: DocumentLibraryRowActionsProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const menuId = `dm-row-actions-${row.id}`;
  const showRetryProcessing =
    !!onRetryProcessing &&
    isDocumentRolledBack(row.upload_status, row.lifecycle_status);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewEdit = () => {
    handleClose();
    onViewEdit(row);
  };

  const handleDelete = () => {
    handleClose();
    onDelete(row);
  };

  const handleRetryProcessing = () => {
    handleClose();
    onRetryProcessing?.(row);
  };

  return (
    <>
      <IconButton
        aria-label={t("opva.actions")}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        disabled={disabled}
        onClick={handleOpen}
        size="small"
        color="primary"
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        marginThreshold={null}
        PaperProps={{ sx: actionMenuPaperSx }}
        MenuListProps={{ dense: true, sx: { py: 0 } }}
      >
        <MenuItem onClick={handleViewEdit} sx={actionMenuItemSx}>
          <Box className="d-flex align-items-center">
            <EditIcon fontSize="small" color="primary" />
            <Typography variant="labelNormal" color="primary" className="ps-2">
              {t("opva.viewEdit")}
            </Typography>
          </Box>
        </MenuItem>
        {showRetryProcessing ? (
          <MenuItem onClick={handleRetryProcessing} sx={{ ...actionMenuItemSx, mt: 1.5 }}>
            <Box className="d-flex align-items-center">
              <ReplayIcon fontSize="small" color="primary" />
              <Typography variant="labelNormal" color="primary" className="ps-2">
                {t("opva.dmRetryProcessing", {
                  defaultValue: "Retry processing",
                })}
              </Typography>
            </Box>
          </MenuItem>
        ) : null}
        <MenuItem onClick={handleDelete} sx={{ ...actionMenuItemSx, mt: 1.5 }}>
          <Box className="d-flex align-items-center">
            <DeleteIcon fontSize="small" color="error" />
            <Typography variant="labelNormal" color="error" className="ps-2">
              {t("opva.delete")}
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
}
